/**
 * Unsigned promoters (migration 1712, owner 2026-11-04).
 *
 * A rep setting up a gun show picks the REAL promoter, or quick-adds it as an UNSIGNED promoter: type promoter, a
 * prospect (no Facility ID / Affiliate ID), no portal access, no partner agreement. The server shows similar
 * organizations first and refuses an exact duplicate (same name + state); the rep must confirm none of the similar
 * ones is the promoter. The "XX No Reseller" organizations are never promoters and never offered.
 */
import { supabase } from "./supabase";

/** Roles the server lets add an unsigned promoter (mirror of _can_add_unsigned_promoter, mig 1712). */
const ADD_ROLES = new Set([
  "superadmin",
  "admin",
  "manager",
  "staff",
  "regional_manager",
  "territory_manager",
]);

export function canAddUnsignedPromoter(
  role: string | null | undefined,
): boolean {
  return !!role && ADD_ROLES.has(role.toLowerCase());
}

export interface SimilarOrganization {
  id: string;
  name: string;
  organization_type: string | null;
  status: string | null;
  state: string | null;
  unsigned: boolean;
  exact: boolean;
  same_state: boolean;
  score: number;
}

export type CreateUnsignedPromoterResult =
  | {
      status: "created";
      organization: { id: string; name: string; state: string };
      shown_candidates: number;
    }
  | {
      status: "exists";
      organization: SimilarOrganization;
      candidates: SimilarOrganization[];
    }
  | { status: "possible_duplicates"; candidates: SimilarOrganization[] }
  | {
      status:
        | "forbidden"
        | "invalid_name"
        | "invalid_state"
        | "invalid_email"
        | "invalid_contact";
    };

export interface CreateUnsignedPromoterInput {
  name: string;
  state: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  /** true only after the rep confirmed none of the similar organizations is this promoter. */
  confirmNoDuplicate?: boolean;
}

export async function createUnsignedPromoter(
  input: CreateUnsignedPromoterInput,
): Promise<CreateUnsignedPromoterResult> {
  const { data, error } = await supabase.rpc("create_unsigned_promoter", {
    p_name: input.name,
    p_state: input.state,
    p_contact_name: input.contactName || null,
    p_contact_email: input.contactEmail || null,
    p_contact_phone: input.contactPhone || null,
    p_confirm_no_duplicate: input.confirmNoDuplicate === true,
  });
  if (error) {
    throw error;
  }
  return data as CreateUnsignedPromoterResult;
}

/** Ids of the promoter organizations that were added as unsigned (badge in the picker). */
export async function listUnsignedPromoterIds(): Promise<Set<string>> {
  const { data, error } = await supabase
    .from("unsigned_promoters")
    .select("organization_id");
  if (error) {
    return new Set();
  }
  return new Set(
    ((data ?? []) as { organization_id: string }[]).map(
      (r) => r.organization_id,
    ),
  );
}

export function createUnsignedPromoterMessage(status: string): string {
  switch (status) {
    case "forbidden":
      return "Your role can't add promoter organizations. Ask an admin or your event lead.";
    case "invalid_name":
      return "Enter the promoter's name (3 to 120 characters).";
    case "invalid_state":
      return "Choose the promoter's state.";
    case "invalid_email":
      return "That contact email doesn't look right.";
    case "invalid_contact":
      return "The contact name or phone is too long.";
    default:
      return "The promoter couldn't be added. Try again.";
  }
}
